import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Building2, Phone, ShieldCheck, Target, Home, RotateCcw, UserRound, Compass, Zap, Users } from "lucide-react";
import { RankBadge } from "@/components/sim/RankBadge";
import { CrisisFooter } from "@/components/sim/CrisisFooter";
import { nextGoal, loadTraineeName, saveTraineeName, loadCohort, saveCohort } from "@/lib/progression";

const MODALITY_ICONS = {
  "In-person": Building2,
  "Phone": Phone,
  "Shelter intake": Home,
};

const MODALITY_TINT = {
  "In-person": "text-emerald-700 bg-emerald-100",
  "Phone": "text-sky-700 bg-sky-100",
  "Shelter intake": "text-teal-700 bg-teal-100",
};

const CARD_ENTER = { opacity: 0, y: 16 };
const CARD_VISIBLE = { opacity: 1, y: 0 };

const CaseCard = ({ scenario: s, index, best, onPick }) => {
  const Icon = MODALITY_ICONS[s.modality] || ShieldCheck;
  const tint = MODALITY_TINT[s.modality] || "text-stone-700 bg-stone-100";
  return (
    <motion.button
      data-testid={`case-${s.id}`}
      onClick={() => onPick(s.id)}
      initial={CARD_ENTER}
      animate={CARD_VISIBLE}
      transition={{ duration: 0.4, delay: 0.08 * index }}
      className="group text-left rounded-2xl border border-stone-200 bg-white p-6 shadow-sm hover:border-emerald-500 hover:shadow-md transition-all duration-300"
    >
      <div className="flex items-center justify-between">
        <span className={`inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full ${tint}`}>
          <Icon className="h-3.5 w-3.5" /> {s.modality}
        </span>
        <span className="text-xs text-stone-400">{s.estimated}</span>
      </div>
      <h2 className="mt-4 font-heading text-xl font-semibold tracking-tight text-stone-900">{s.title}</h2>
      {s.id === "third-party-call" && (
        <span data-testid="quick-case-badge" className="mt-2 inline-flex items-center gap-1 rounded-full bg-amber-100 text-amber-800 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider">
          <Zap className="h-3 w-3" /> Quick case (about 4 minutes)
        </span>
      )}
      <p className="mt-2 text-sm leading-relaxed text-stone-600">{s.subtitle}</p>
      <div className="mt-5 flex items-center justify-between border-t border-stone-100 pt-4">
        <span className="text-xs text-stone-400">
          {s.decisions_count} decisions
          {best != null && (
            <span data-testid={`best-${s.id}`} className="ml-2 font-semibold text-emerald-700">· Best {best}%</span>
          )}
        </span>
        <span className="inline-flex items-center gap-1 text-sm font-medium text-emerald-800 group-hover:gap-2 transition-all duration-300">
          {best != null ? "Replay" : "Begin"} <ArrowRight className="h-4 w-4" />
        </span>
      </div>
    </motion.button>
  );
};

export const CaseSelect = ({ scenarios, rank = "trainee", progress = { cases: {} }, knowledge = {}, preCheckAvailable = false, onPreCheck, postCheckReady = false, onPostCheck, onPick, onBack, onResetProgress, exploring = false, onToggleExploring }) => {
  const hasProgress = rank !== "trainee" || Object.keys(progress.cases || {}).length > 0;
  const [name, setName] = useState(loadTraineeName());
  const [cohort, setCohort] = useState(loadCohort());
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
      <button
        data-testid="back-to-intro"
        onClick={onBack}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-stone-500 hover:text-stone-800 transition-colors duration-200"
      >
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="mt-6"
      >
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-800">Choose your case</p>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-stone-900">
            Which shift will you take?
          </h1>
          <RankBadge rank={rank} />
        </div>
        <p className="mt-3 text-base sm:text-lg text-stone-600 max-w-2xl">
          Each case is a different survivor, modality, and set of pressures. Your choices are
          shuffled every time, so there’s no memorizing “the right letter,” only survivor-centered judgment.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <label className="inline-flex items-center gap-2 rounded-xl border border-stone-200 bg-white px-4 py-2 focus-within:border-emerald-500 transition-colors duration-200">
            <UserRound className="h-4 w-4 text-stone-400" />
            <input
              data-testid="trainee-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onBlur={() => saveTraineeName(name)}
              placeholder="Your name (shown on reports)"
              maxLength={60}
              className="w-56 bg-transparent text-sm text-stone-800 placeholder:text-stone-400 outline-none"
            />
          </label>
          <label className="inline-flex items-center gap-2 rounded-xl border border-stone-200 bg-white px-4 py-2 focus-within:border-emerald-500 transition-colors duration-200">
            <Users className="h-4 w-4 text-stone-400" />
            <input
              data-testid="cohort-code-input"
              value={cohort}
              onChange={(e) => setCohort(e.target.value)}
              onBlur={() => saveCohort(cohort)}
              placeholder="Cohort code (optional)"
              maxLength={40}
              aria-label="Cohort code, optional, provided by your facilitator"
              className="w-44 bg-transparent text-sm text-stone-800 placeholder:text-stone-400 outline-none"
            />
          </label>
          <button
            data-testid="exploring-toggle"
            onClick={() => onToggleExploring?.(!exploring)}
            aria-pressed={exploring}
            className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-medium transition-colors duration-200 ${
              exploring
                ? "border-sky-300 bg-sky-50 text-sky-900"
                : "border-stone-200 bg-white text-stone-500 hover:border-sky-300 hover:text-sky-800"
            }`}
          >
            <Compass className="h-4 w-4" />
            {exploring ? "Exploring: nothing is recorded" : "Just exploring? Try a case"}
          </button>
          <div className="inline-flex items-center gap-2 rounded-xl bg-emerald-50 border border-emerald-200 px-4 py-2.5">
            <Target className="h-4 w-4 text-emerald-700" />
            <span data-testid="next-goal" className="text-sm text-emerald-900">{nextGoal(rank)}</span>
          </div>
          {hasProgress && (
            <button
              data-testid="reset-progress-button"
              onClick={() => {
                if (window.confirm("Reset all progress? Your rank and best case scores will be cleared, and you will start over as a Trainee Counselor.")) onResetProgress();
              }}
              className="inline-flex items-center gap-1.5 rounded-xl border border-stone-200 bg-white px-4 py-2.5 text-sm font-medium text-stone-500 hover:border-rose-300 hover:text-rose-700 transition-colors duration-200"
            >
              <RotateCcw className="h-4 w-4" /> Reset all progress
            </button>
          )}
        </div>
      </motion.div>

      {preCheckAvailable && (
        <div data-testid="baseline-check-banner" className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-stone-200 bg-white px-6 py-5">
          <div>
            <p className="font-heading text-lg font-semibold text-stone-900">First shift? Take an optional baseline check.</p>
            <p className="mt-1 text-sm text-stone-600">Ten quick questions before you start. There is no pass or fail; it simply lets you see how far you come by the end of training. You can also skip it and jump straight into a case.</p>
          </div>
          <button
            data-testid="start-baseline-check-button"
            onClick={onPreCheck}
            className="inline-flex items-center gap-1.5 rounded-full border border-emerald-600 text-emerald-800 hover:bg-emerald-50 px-6 py-2.5 text-sm font-medium transition-colors duration-200"
          >
            Take the baseline check <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {postCheckReady && (
        <div data-testid="post-check-banner" className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-emerald-300 bg-emerald-50 px-6 py-5">
          <div>
            <p className="font-heading text-lg font-semibold text-emerald-900">You have completed every case.</p>
            <p className="mt-1 text-sm text-emerald-800">Take the final knowledge check, the same ten questions from before your first case, and see how far you have come.</p>
          </div>
          <button
            data-testid="start-post-check-button"
            onClick={onPostCheck}
            className="inline-flex items-center gap-1.5 rounded-full bg-emerald-700 hover:bg-emerald-800 px-6 py-2.5 text-sm font-medium text-white transition-colors duration-200"
          >
            Start final check <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      )}

      {knowledge?.pre && knowledge?.post && (
        <p data-testid="knowledge-summary" className="mt-6 inline-flex items-center gap-2 rounded-xl border border-stone-200 bg-white px-4 py-2.5 text-sm text-stone-600">
          <ShieldCheck className="h-4 w-4 text-emerald-700" />
          Knowledge check: {knowledge.pre.score} of {knowledge.pre.total} before your shift, {knowledge.post.score} of {knowledge.post.total} after.
        </p>
      )}

      <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {scenarios.map((s, i) => (
          <CaseCard key={s.id} scenario={s} index={i} best={progress.cases[s.id]} onPick={onPick} />
        ))}
      </div>

      <CrisisFooter className="mt-12" />
    </div>
  );
};
